<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, requiredIf } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

// Wizard for Channel::Sip — one trunk/extension = one inbox (see
// docs/voice-calling/README.md decision #4). Credentials here are
// per-trunk (host/port/transport/username/password), unlike WhatsappLite's
// shared account-level evolution_api_url/key — mirrors Twilio.vue's
// per-channel credential form instead.
export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      sipHost: '',
      sipPort: 5060,
      sipTransport: 'udp',
      sipUsername: '',
      sipPassword: '',
      phoneNumber: '',
      register: true,
      inboundCallsEnabled: true,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations() {
    return {
      inboxName: { required },
      sipHost: { required },
      sipPort: { required },
      // A registered trunk authenticates with username/password; a static
      // (IP-authenticated) trunk skips REGISTER entirely and doesn't need them.
      sipUsername: { required: requiredIf(() => this.register) },
      sipPassword: { required: requiredIf(() => this.register) },
    };
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const sipChannel = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName?.trim(),
          channel: {
            type: 'sip',
            trunk_name: this.inboxName?.trim(),
            sip_host: this.sipHost?.trim(),
            sip_port: Number(this.sipPort),
            sip_transport: this.sipTransport,
            sip_username: this.sipUsername?.trim(),
            sip_password: this.sipPassword,
            phone_number: this.phoneNumber?.trim() || null,
            register: this.register,
            inbound_calls_enabled: this.inboundCallsEnabled,
          },
        });

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: sipChannel.id,
          },
        });
      } catch (error) {
        const errorMessage =
          parseAPIErrorResponse(error) ||
          this.$t('INBOX_MGMT.ADD.SIP_CHANNEL.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.SIP_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.SIP_CHANNEL.DESC')"
    />

    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.SIP_CHANNEL.INBOX_NAME.PLACEHOLDER')
            "
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.INBOX_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.sipHost.$error }">
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_HOST.LABEL') }}
          <input
            v-model="sipHost"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_HOST.PLACEHOLDER')"
            @blur="v$.sipHost.$touch"
          />
          <span v-if="v$.sipHost.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_HOST.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex gap-4">
        <label :class="{ error: v$.sipPort.$error }" class="flex-1">
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_PORT.LABEL') }}
          <input
            v-model="sipPort"
            type="number"
            :placeholder="$t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_PORT.PLACEHOLDER')"
            @blur="v$.sipPort.$touch"
          />
          <span v-if="v$.sipPort.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_PORT.ERROR') }}
          </span>
        </label>

        <label class="flex-1">
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_TRANSPORT.LABEL') }}
          <select v-model="sipTransport">
            <option value="udp">
              {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_TRANSPORT.UDP') }}
            </option>
            <option value="tcp">
              {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_TRANSPORT.TCP') }}
            </option>
            <option value="tls">
              {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_TRANSPORT.TLS') }}
            </option>
          </select>
        </label>
      </div>

      <div class="max-w-[65%] w-full">
        <label for="register-toggle">
          <input
            id="register-toggle"
            v-model="register"
            type="checkbox"
            class="checkbox"
          />
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.REGISTER.LABEL') }}
        </label>
        <p class="text-n-slate-11 text-sm mt-0">
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.REGISTER.HELP') }}
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.sipUsername.$error }">
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_USERNAME.LABEL') }}
          <input
            v-model="sipUsername"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_USERNAME.PLACEHOLDER')
            "
            @blur="v$.sipUsername.$touch"
          />
          <span v-if="v$.sipUsername.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_USERNAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.sipPassword.$error }">
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_PASSWORD.LABEL') }}
          <input
            v-model="sipPassword"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_PASSWORD.PLACEHOLDER')
            "
            @blur="v$.sipPassword.$touch"
          />
          <span v-if="v$.sipPassword.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.SIP_PASSWORD.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.PHONE_NUMBER.LABEL') }}
          <input
            v-model="phoneNumber"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.SIP_CHANNEL.PHONE_NUMBER.PLACEHOLDER')
            "
          />
        </label>
      </div>

      <div class="max-w-[65%] w-full">
        <label for="inbound-calls-toggle">
          <input
            id="inbound-calls-toggle"
            v-model="inboundCallsEnabled"
            type="checkbox"
            class="checkbox"
          />
          {{ $t('INBOX_MGMT.ADD.SIP_CHANNEL.INBOUND_CALLS.LABEL') }}
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.SIP_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
